import Head from 'expo-router/head';
import { useState } from 'react';
import { ScrollView, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';

import { listSeedScript } from '@/components/detail/list-seed';
import { EventRow } from '@/components/riviera/city-events-screen';
import { EventCard } from '@/components/riviera/event-card';
import { EventSliceChips } from '@/components/riviera/event-slice-chips';
import {
  MoreLink,
  SectionHeading,
  StatChip,
  landingStyles as styles,
} from '@/components/riviera/landing-parts';
import { SiteFooter } from '@/components/riviera/site-footer';
import { SiteHeader } from '@/components/riviera/site-header';
import { Fonts, Spacing } from '@/constants/theme';
import { useEventSlice } from '@/hooks/use-event-slice';
import { useTheme } from '@/hooks/use-theme';
import {
  MIN_EVENTS_PER_CATEGORY_PAGE,
  TOP_EVENTS_ON_SLICE_PAGE,
  isWindowSlice,
  type CityEventCategoryPages,
  type EventSliceSeed,
} from '@/lib/city-event-slices';
import { eventCityStats } from '@todo/core/city-listings';
import { APP_TZ } from '@todo/core/date-range';
import { cities } from '@/lib/events-data';
import { cityEventsCopy, cityInPhrase } from '@/lib/i18n/city-listings-content';
import { localizedTitle } from '@todo/core/i18n/event-content';
import { formatDateCompact, formatDayMonth, formatWeekday } from '@todo/core/i18n/format';
import type { Locale } from '@todo/core/i18n/locales';
import { useLocale } from '@todo/core/i18n/use-locale';
import { usableImageUrl } from '@todo/core/image-url';
import {
  CITY_EVENT_CATEGORY_PAGES_SEED_KEY,
  CITY_LISTING_PAGES_SEED_KEY,
  LIST_BUCKET,
  cityEventSliceSeedKey,
} from '@todo/core/list-queries';
import {
  SITE_NAME,
  SITE_URL,
  absoluteUrl,
  breadcrumbJsonLd,
  hreflangLinks,
  itemListJsonLd,
  richResultImage,
  seoTitle,
  truncate,
} from '@todo/core/seo';
import { getPrefetched } from '@todo/core/static-prefetch';

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** How many titles the answer-first "Highlights" line names. */
const HIGHLIGHTS = 3;

const DAY_MS = 24 * 60 * 60 * 1000;

/** "Sat 24 Oct", or "Sat 24 Oct – Sun 25 Oct" for a window spanning several days. */
function windowRange(seed: EventSliceSeed, locale: Locale): string {
  if (!seed.from || !seed.until) return '';
  const day = (date: Date) =>
    `${formatWeekday(date, APP_TZ, locale)} ${formatDayMonth(date, APP_TZ, locale)}`;
  const first = new Date(seed.from);
  // `until` is exclusive (the next midnight); its last day starts a day before.
  const last = new Date(new Date(seed.until).getTime() - DAY_MS);
  const a = day(first);
  const b = day(last);
  return a === b ? a : `${a} – ${b}`;
}

function windowTitle(t: Translate, slice: string, inCity: string): string {
  return t(`cityEventSlices.windows.${slice}.title`, { inCity });
}

function categoryTitle(t: Translate, id: string, inCity: string): string {
  return t('cityEventSlices.categoryTitle', {
    category: t(`categories.${id}`, { defaultValue: t('categories.other') }),
    inCity,
  });
}

/**
 * The /[lang]/[city]/events/[slice] landing pages: one city's events in a date
 * window (/nice/events/this-weekend) or one category (/nice/events/concerts).
 * Built for "what's on in nice this weekend" / "concert nice" searches — and
 * for AI assistants answering them, which need a page that says, up front, how
 * many events there are and which ones, with a visible "updated" date. A
 * window page is rebuilt by the daily deploy (and refreshed in the browser if
 * a deploy was missed — useEventSlice); an empty one offers the city's next
 * events instead of a dead end. See src/lib/city-event-slices.ts.
 */
export function CityEventSliceScreen({ cityId, slice }: { cityId: string; slice: string }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const city = cities.find((c) => c.id === cityId);
  const copy = city ? cityEventsCopy(locale, city.id) : undefined;
  const inCity = city ? cityInPhrase(locale, city.id) : undefined;
  const window = isWindowSlice(slice);
  const [categoryPages] = useState(
    () => getPrefetched<CityEventCategoryPages>(LIST_BUCKET, CITY_EVENT_CATEGORY_PAGES_SEED_KEY) ?? {},
  );
  // Always called (rules of hooks); an unknown city/slice returns no rows.
  const { seed, loading, error } = useEventSlice(cityId, slice, locale);

  // A category the export didn't generate a page for only arrives via a bad
  // client-side navigation; outside an export (no baked list) any is allowed.
  const knownSlice =
    window ||
    (Object.keys(categoryPages).length === 0
      ? slice !== 'other' && t(`categories.${slice}`, { defaultValue: '' }) !== ''
      : (categoryPages[cityId] ?? []).includes(slice));

  if (!city || !copy || !inCity || !knownSlice) {
    return (
      <View style={[styles.root, { backgroundColor: theme.background }]}>
        <Head key={locale}>
          <title>{`${t('common.notFound')} — ${SITE_NAME}`}</title>
          <meta name="robots" content="noindex, follow" />
        </Head>
        <SafeAreaView edges={['top']} style={{ backgroundColor: theme.card }}>
          <SiteHeader />
        </SafeAreaView>
        <Text style={[styles.status, { color: theme.mutedForeground, paddingHorizontal: Spacing.three }]}>
          {t('common.notFound')}
        </Text>
      </View>
    );
  }

  const rows = seed?.rows ?? [];
  const next = seed?.next ?? [];
  const stats = eventCityStats(rows);
  const path = `${city.id}/events/${slice}`;
  const canonical = absoluteUrl(locale, path);
  const heading = window ? windowTitle(t, slice, inCity) : categoryTitle(t, slice, inCity);
  const title = seoTitle(heading);
  const when = window ? t(`cityEventSlices.windows.${slice}.when`) : '';
  const range = seed && window ? windowRange(seed, locale) : '';
  const sep = t('cityEvents.listSeparator');

  // The answer first: how many, when, and which — in the page's opening lines
  // and its meta description alike.
  const answer = window
    ? rows.length > 0
      ? `${t('cityEventSlices.windowCount', { count: rows.length, inCity, when })}${range ? ` (${range})` : ''}.`
      : t('cityEventSlices.empty', { inCity, when: range ? `${when} (${range})` : when })
    : rows.length > 0 && stats.firstStart && stats.lastStart
      ? t('cityEventSlices.categoryAnswer', {
          title: heading,
          stats: t('cityEvents.stats', { count: rows.length }),
          range: t('cityEventSlices.range', {
            first: formatDateCompact(stats.firstStart, APP_TZ, locale),
            last: formatDateCompact(stats.lastStart, APP_TZ, locale),
          }),
        })
      : '';
  const highlights =
    rows.length > 0
      ? t('cityEventSlices.highlights', {
          list: rows
            .slice(0, HIGHLIGHTS)
            .map((event) => localizedTitle(event, locale))
            .join(sep),
        })
      : '';
  const metaDescription = truncate([answer, highlights].filter(Boolean).join(' ') || copy.intro[0]);
  const updated = seed ? formatDateCompact(seed.builtAt, APP_TZ, locale) : null;

  const top = rows.slice(0, TOP_EVENTS_ON_SLICE_PAGE);
  const rest = rows.slice(TOP_EVENTS_ON_SLICE_PAGE);
  const ogImage = richResultImage(
    [...top, ...next].find((event) => usableImageUrl(event.image_url))?.image_url,
  );
  // A category page below its export floor is thin — see
  // MIN_EVENTS_PER_CATEGORY_PAGE. A window page stays indexable even when
  // empty: its URL is stable, only its content moves day to day, and it still
  // points at the next events.
  const thin = !window && !loading && rows.length < MIN_EVENTS_PER_CATEGORY_PAGE;

  return (
    <View style={[styles.root, { backgroundColor: theme.background }]}>
      {/* key forces a fresh mount on locale change — see the comment in
          app/[lang]/_layout.tsx for why. */}
      <Head key={locale}>
        <title>{title}</title>
        <meta name="description" content={metaDescription} />
        {thin ? <meta name="robots" content="noindex, follow" /> : null}
        <link rel="canonical" href={canonical} />
        {hreflangLinks(path).map((link) => (
          <link key={link.hrefLang} rel="alternate" hrefLang={link.hrefLang} href={link.href} />
        ))}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={canonical} />
        {ogImage ? <meta property="og:image" content={ogImage} /> : null}
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={metaDescription} />
        {ogImage ? <meta name="twitter:image" content={ogImage} /> : null}
        <script type="application/ld+json">
          {JSON.stringify(
            breadcrumbJsonLd(locale, [
              { name: t('nav.cities'), path: 'cities' },
              { name: city.name, path: city.id },
              { name: copy.title, path: `${city.id}/events` },
              { name: heading },
            ]),
          )}
        </script>
        {seed ? (
          <script type="application/ld+json">
            {JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'CollectionPage',
              name: heading,
              url: canonical,
              inLanguage: locale,
              description: metaDescription,
              dateModified: seed.builtAt,
              isPartOf: { '@id': `${SITE_URL}/#website` },
            })}
          </script>
        ) : null}
        {top.length > 0 ? (
          <script type="application/ld+json">
            {JSON.stringify(
              itemListJsonLd(
                rows.slice(0, 20).map((event) => ({
                  name: localizedTitle(event, locale),
                  url: absoluteUrl(locale, `events/${event.show_slug}`),
                })),
                canonical,
              ),
            )}
          </script>
        ) : null}
        {/* Baked rows so the client's first render matches this static HTML. */}
        {listSeedScript(cityEventSliceSeedKey(city.id, slice, locale))}
        {listSeedScript(CITY_EVENT_CATEGORY_PAGES_SEED_KEY)}
        {listSeedScript(CITY_LISTING_PAGES_SEED_KEY)}
      </Head>
      <SafeAreaView edges={['top']} style={{ backgroundColor: theme.card }}>
        <SiteHeader />
      </SafeAreaView>
      <ScrollView style={{ flex: 1 }}>
        <View style={styles.centered}>
          <View style={styles.page}>
            <Text
              role="heading"
              aria-level={1}
              style={[styles.heading, { color: theme.text, fontFamily: Fonts?.serif }]}>
              {heading}
            </Text>
            {updated ? (
              <Text style={[styles.stats, { color: theme.gold }]}>
                {t('cityEventSlices.updated', { date: updated })}
              </Text>
            ) : null}
            {answer ? <Text style={[styles.intro, { color: theme.text }]}>{answer}</Text> : null}
            {highlights ? (
              <Text style={[styles.intro, { color: theme.text }]}>{highlights}</Text>
            ) : null}

            <EventSliceChips cityId={city.id} active={slice} inCity={inCity} />

            {loading ? (
              <Text style={[styles.status, { color: theme.mutedForeground }]}>
                {t('cityEventSlices.loading')}
              </Text>
            ) : error && rows.length === 0 ? (
              <Text style={[styles.status, { color: theme.destructive }]}>{error}</Text>
            ) : rows.length === 0 ? (
              next.length > 0 ? (
                <View style={styles.section}>
                  <SectionHeading>{t('cityEvents.nextHeading', { inCity })}</SectionHeading>
                  <View style={styles.grid}>
                    {next.map((event, index) => (
                      <EventCard key={event.id} event={event} eager={index < 4} />
                    ))}
                  </View>
                </View>
              ) : null
            ) : (
              <>
                <View style={styles.section}>
                  <SectionHeading>{t('cityEventSlices.allHeading', { count: rows.length })}</SectionHeading>
                  <View style={styles.grid}>
                    {top.map((event, index) => (
                      <EventCard key={event.id} event={event} eager={index < 4} />
                    ))}
                  </View>
                  {rest.length > 0 ? (
                    <View>
                      {rest.map((event) => (
                        <EventRow key={event.id} event={event} locale={locale} hidden={false} t={t} />
                      ))}
                    </View>
                  ) : null}
                </View>

                {stats.venues.length > 0 ? (
                  <View style={styles.section}>
                    <SectionHeading>{t('cityEvents.venuesHeading')}</SectionHeading>
                    <View style={styles.chipsRow}>
                      {stats.venues.slice(0, 8).map((venue) => (
                        <StatChip key={venue.name} label={`${venue.name} · ${venue.count}`} />
                      ))}
                    </View>
                  </View>
                ) : null}
              </>
            )}

            <View style={styles.moreLinks}>
              <MoreLink
                label={copy.title}
                href={{ pathname: '/[lang]/[city]/events', params: { lang: locale, city: city.id } }}
              />
              <MoreLink
                label={t('cityEvents.allEvents')}
                href={{ pathname: '/[lang]/events', params: { lang: locale } }}
              />
            </View>
          </View>
        </View>
        <SiteFooter />
      </ScrollView>
    </View>
  );
}
